import React, { useEffect, useRef, useState } from "react";
import { Menu, ChevronDown, KeyRound, LogOut, User, CreditCard, HelpCircle } from "lucide-react";
import axios from "../axios";
import endpoint from "../endpoints";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { getUser, clearSession } from "../utils/auth";
import { ThemeToggle, ThemeSegmentedControl } from "./ui/ThemeToggle";
import { WhatsAppAccountSwitcher } from "./ui/WhatsAppAccountSwitcher";

const titles = {
  "/dashboard":      "Dashboard",
  "/account":        "WhatsApp Accounts",
  "/staff":          "Team & Staff",
  "/team":           "Team & Staff",
  "/chat":           "Chat",
  "/campaigns":      "Campaigns",
  "/template":       "Templates",
  "/contact":        "Contacts",
  "/followups":      "Follow-Ups Hub",
  "/group":          "Groups",
  "/importcontact":  "Import Contacts",
  "/billing":        "Billing",
  "/profile":        "Profile",
  "/change-password":"Change Password",
  "/inbox":          "Team Inbox",
  "/auto-replies":   "Auto Replies",
  "/quick-replies":  "Quick Replies",
  "/developer":      "Developer / API",
  "/activity-log":   "Activity Log",
  "/api-keys":       "API Keys",
  "/support":        "Help & Support",
};

const Navbar = ({ toggleSidebar }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [company, setCompany] = useState(() => {
    try { return JSON.parse(localStorage.getItem("company")) || null; } catch { return null; }
  });
  const menuRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const user = getUser();

  // Fetch company profile once on mount
  useEffect(() => {
    axios.get(endpoint.PROFILE).then((res) => {
      const c = res.data?.data?.company;
      if (c) {
        setCompany(c);
        localStorage.setItem("company", JSON.stringify(c));
      }
    }).catch(() => {});
  }, []);

  // Re-sync when profile page fires a custom event after save
  useEffect(() => {
    const handler = (e) => {
      setCompany(e.detail);
      localStorage.setItem("company", JSON.stringify(e.detail));
    };
    window.addEventListener("company:updated", handler);
    return () => window.removeEventListener("company:updated", handler);
  }, []);

  const logoUrl = company?.logo
    ? `${endpoint.url}/${company.logo}`.replace(/([^:])(\/\/+)/g, "$1/")
    : null;

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const authLogout = async () => {
    try {
      await axios.post(endpoint.LOGOUT);
    } catch {
      // ignore - clear session regardless
    } finally {
      clearSession();
      toast.success("Logged out successfully");
      navigate("/");
    }
  };

  return (
    <header className="sticky top-0 z-20 flex h-14 items-center justify-between border-b border-ink-100 bg-white/90 px-4 backdrop-blur-md dark:border-ink-800 dark:bg-ink-900/90 transition-colors duration-200">
      <div className="flex items-center gap-3">
        <button
          onClick={toggleSidebar}
          aria-label="Open menu"
          className="rounded-lg p-2 text-ink-500 hover:bg-ink-100 dark:text-ink-400 dark:hover:bg-ink-800 transition-colors lg:hidden"
        >
          <Menu className="h-5 w-5" />
        </button>
        <h1 key={location.pathname} className="text-[15px] font-semibold text-ink-800 dark:text-ink-100 animate-fade-in">
          {titles[location.pathname] || "WhatsSend"}
        </h1>
      </div>

      <div className="flex items-center gap-2 sm:gap-3">
        {/* WhatsApp Multi-Account Switcher & Filter */}
        <WhatsAppAccountSwitcher variant="navbar" />

        {/* Quick Theme Switcher button */}
        <ThemeToggle />

        {/* User Account Menu */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-ink-50 dark:hover:bg-ink-800 transition-colors"
          >
            {logoUrl ? (
              <img
                src={logoUrl}
                alt="logo"
                className="h-8 w-8 rounded-full object-cover border border-ink-200 dark:border-ink-700"
              />
            ) : (
              <div className="h-8 w-8 rounded-full bg-brand-100 dark:bg-brand-950/70 flex items-center justify-center text-brand-700 dark:text-brand-300 text-sm font-bold border border-ink-200 dark:border-ink-700">
                {(company?.name || user?.name || "A").charAt(0).toUpperCase()}
              </div>
            )}
            <span className="hidden text-sm font-medium text-ink-700 dark:text-ink-200 sm:block">
              {company?.name || user?.name || "Account"}
            </span>
            <ChevronDown className="h-4 w-4 text-ink-400 dark:text-ink-500" />
          </button>

          {menuOpen && (
            <div className="absolute right-0 mt-2 w-64 max-w-[calc(100vw-1.5rem)] rounded-2xl border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 py-2 shadow-2xl animate-fade-in z-50">
              <div className="px-3.5 py-2 border-b border-slate-100 dark:border-slate-800">
                <p className="text-xs font-bold text-slate-400 dark:text-slate-400 mb-2">Theme</p>
                <ThemeSegmentedControl className="w-full" />
              </div>

              <Link
                to="/profile"
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-2.5 px-3.5 py-2.5 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              >
                <User className="h-4 w-4 text-slate-400" /> Profile
              </Link>
              {(user?.role === "owner" || user?.role === "admin") && (
                <Link
                  to="/billing"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-2.5 px-3.5 py-2.5 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                >
                  <CreditCard className="h-4 w-4 text-slate-400" /> Billing
                </Link>
              )}
              <Link
                to="/change-password"
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-2.5 px-3.5 py-2.5 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              >
                <KeyRound className="h-4 w-4 text-slate-400" /> Change Password
              </Link>
              <Link
                to="/support"
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-2.5 px-3.5 py-2.5 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              >
                <HelpCircle className="h-4 w-4 text-slate-400" /> Help & Support
              </Link>
              <div className="my-1 border-t border-slate-100 dark:border-slate-800" />
              <button
                onClick={authLogout}
                className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-xs font-bold text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40 transition-colors"
              >
                <LogOut className="h-4 w-4 text-rose-500" /> Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Navbar;

